import { useMutation, useQuery } from "@tanstack/react-query";
import {
  dismissNotification,
  getNotifications,
  markNotificationsRead,
} from "@/lib/api";
import { queryClient, useScopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import type { NotificationsPayload } from "@/lib/types";

function invalidateNotifications() {
  void queryClient.invalidateQueries({
    predicate: (q) => (q.queryKey as unknown[])[0] === "notifications",
  });
}

export function useNotifications() {
  const signedIn = useAuthStore((s) => !!s.user);
  const queryKey = useScopedKey("notifications");
  const query = useQuery<NotificationsPayload>({
    queryKey,
    queryFn: getNotifications,
    enabled: signedIn,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const readMutation = useMutation({
    mutationFn: (body: { keys?: string[]; all_current?: boolean }) =>
      markNotificationsRead(body),
    onSuccess: invalidateNotifications,
  });
  const dismissMutation = useMutation({
    mutationFn: (key: string) => dismissNotification(key),
    onSuccess: invalidateNotifications,
  });

  return {
    ...query,
    markRead: (keys: string[]) => readMutation.mutateAsync({ keys }),
    markAllRead: () => readMutation.mutateAsync({ all_current: true }),
    dismiss: (key: string) => dismissMutation.mutateAsync(key),
    isMutating: readMutation.isPending || dismissMutation.isPending,
  };
}
